/**
 * Projectile rendering.
 *
 * Everything is drawn from three pooled instanced meshes plus one pooled trail
 * buffer, so a busy exchange costs the same handful of draw calls as a quiet
 * one. Nothing here allocates per frame.
 *
 * Readability rule from the brief: a projectile's size and colour are identical
 * at every graphics preset, and an incoming shot always gets its warning ring.
 * Only the decorative sparks scale with quality.
 */
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { scene as bus } from '../scene/sceneBus';
import { glowTexture } from '../assets/textures';

const MAX_PROJECTILES = 32;
const TRAIL_SEGMENTS = 14;

/** Blend target for the impact-hold flare. Module scope: never allocate here. */
const WHITE = new THREE.Color('#ffffff');

export function Projectiles() {
  const coreRef = useRef<THREE.InstancedMesh>(null);
  const haloRef = useRef<THREE.InstancedMesh>(null);
  const shardRef = useRef<THREE.InstancedMesh>(null);
  const trailRef = useRef<THREE.LineSegments>(null);
  const lightsRef = useRef<THREE.PointLight[]>([]);

  const glow = useMemo(() => glowTexture(), []);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const color = useMemo(() => new THREE.Color(), []);

  // Per-projectile trail history, keyed by id, reused across frames.
  const history = useRef(new Map<string, THREE.Vector3[]>());

  const trailGeometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const positions = new Float32Array(MAX_PROJECTILES * TRAIL_SEGMENTS * 2 * 3);
    const colors = new Float32Array(MAX_PROJECTILES * TRAIL_SEGMENTS * 2 * 3);
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return g;
  }, []);

  useFrame((state, delta) => {
    const core = coreRef.current;
    const halo = haloRef.current;
    const shard = shardRef.current;
    if (!core || !halo || !shard) return;

    const list = bus.projectiles;
    const count = Math.min(list.length, MAX_PROJECTILES);
    const t = state.clock.elapsedTime;

    // Drop history for projectiles that are gone.
    const alive = new Set(list.map((p) => p.id));
    for (const key of history.current.keys()) {
      if (!alive.has(key)) history.current.delete(key);
    }

    const trailPos = trailGeometry.getAttribute('position') as THREE.BufferAttribute;
    const trailCol = trailGeometry.getAttribute('color') as THREE.BufferAttribute;
    const posArr = trailPos.array as Float32Array;
    const colArr = trailCol.array as Float32Array;
    let vertex = 0;

    let maxDanger = 0;

    for (let i = 0; i < count; i++) {
      const p = list[i];
      color.set(p.color);

      // A shot that has arrived and is holding for a late parry reads as
      // *stopped and straining* rather than travelling, so the last-chance
      // window is legible from the projectile itself and not only from the
      // reticle ring. Kept deliberately restrained: at point blank the orb
      // already fills much of the frame, and a bright swell on top of that
      // whites out the very indicator the player needs to read.
      const holding = p.resolveIn > 0;
      if (holding) color.lerp(WHITE, 0.16 + Math.sin(t * 34) * 0.08);

      // --- core orb ------------------------------------------------------
      const pulse = holding
        ? 1.04 + Math.sin(t * 34) * 0.05
        : 1 + Math.sin(t * 14 + i) * 0.06;
      dummy.position.set(p.x, p.y, p.z);
      dummy.rotation.set(t * 2.1 + i, t * 1.7, 0);
      dummy.scale.setScalar(p.radius * pulse);
      dummy.updateMatrix();
      core.setMatrixAt(i, dummy.matrix);
      core.setColorAt(i, color);

      // --- halo ----------------------------------------------------------
      dummy.scale.setScalar(p.radius * 2.6 * pulse);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      halo.setMatrixAt(i, dummy.matrix);
      halo.setColorAt(i, color);

      // --- leading shard, oriented along travel --------------------------
      const speed = Math.hypot(p.vx, p.vy, p.vz) || 1;
      dummy.position.set(p.x + (p.vx / speed) * p.radius * 0.6, p.y + (p.vy / speed) * p.radius * 0.6, p.z + (p.vz / speed) * p.radius * 0.6);
      dummy.lookAt(p.x + p.vx, p.y + p.vy, p.z + p.vz);
      dummy.scale.set(p.radius * 0.55, p.radius * 0.55, p.radius * 1.9);
      dummy.updateMatrix();
      shard.setMatrixAt(i, dummy.matrix);
      shard.setColorAt(i, color);

      // --- trail ---------------------------------------------------------
      let hist = history.current.get(p.id);
      if (!hist) {
        hist = [];
        history.current.set(p.id, hist);
      }
      hist.push(new THREE.Vector3(p.x, p.y, p.z));
      if (hist.length > TRAIL_SEGMENTS + 1) hist.shift();
      for (let s = 1; s < hist.length; s++) {
        const a = hist[s - 1];
        const b = hist[s];
        const fade = s / hist.length;
        posArr[vertex * 3] = a.x;
        posArr[vertex * 3 + 1] = a.y;
        posArr[vertex * 3 + 2] = a.z;
        colArr[vertex * 3] = color.r * fade;
        colArr[vertex * 3 + 1] = color.g * fade;
        colArr[vertex * 3 + 2] = color.b * fade;
        vertex++;
        posArr[vertex * 3] = b.x;
        posArr[vertex * 3 + 1] = b.y;
        posArr[vertex * 3 + 2] = b.z;
        colArr[vertex * 3] = color.r * fade;
        colArr[vertex * 3 + 1] = color.g * fade;
        colArr[vertex * 3 + 2] = color.b * fade;
        vertex++;
      }

      // --- dynamic light for the nearest few -----------------------------
      const light = lightsRef.current[i];
      if (light) {
        light.position.set(p.x, p.y, p.z);
        light.color.copy(color);
        light.intensity = 3.4;
        light.distance = 7;
        light.visible = true;
      }

      if (p.incoming) {
        // Danger climbs as the shot closes; drives the warning ring and the
        // arena's swelling duel light.
        const d = Math.max(0, 1 - p.eta / 1.4);
        if (d > maxDanger) maxDanger = d;
      }
    }

    // Park unused instances far away rather than resizing the buffer.
    for (let i = count; i < MAX_PROJECTILES; i++) {
      dummy.position.set(0, -1000, 0);
      dummy.scale.setScalar(0.0001);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      core.setMatrixAt(i, dummy.matrix);
      halo.setMatrixAt(i, dummy.matrix);
      shard.setMatrixAt(i, dummy.matrix);
      const light = lightsRef.current[i];
      if (light) light.visible = false;
    }
    for (let v = vertex; v < MAX_PROJECTILES * TRAIL_SEGMENTS * 2; v++) {
      posArr[v * 3] = 0;
      posArr[v * 3 + 1] = -1000;
      posArr[v * 3 + 2] = 0;
    }

    core.instanceMatrix.needsUpdate = true;
    halo.instanceMatrix.needsUpdate = true;
    shard.instanceMatrix.needsUpdate = true;
    if (core.instanceColor) core.instanceColor.needsUpdate = true;
    if (halo.instanceColor) halo.instanceColor.needsUpdate = true;
    if (shard.instanceColor) shard.instanceColor.needsUpdate = true;
    trailPos.needsUpdate = true;
    trailCol.needsUpdate = true;
    if (trailRef.current) trailRef.current.visible = count > 0;

    bus.danger += (maxDanger - bus.danger) * Math.min(1, delta * 12);
  });

  return (
    <group>
      <instancedMesh ref={coreRef} args={[undefined, undefined, MAX_PROJECTILES]} frustumCulled={false}>
        <icosahedronGeometry args={[1, 1]} />
        <meshBasicMaterial toneMapped={false} />
      </instancedMesh>

      <instancedMesh ref={haloRef} args={[undefined, undefined, MAX_PROJECTILES]} frustumCulled={false}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          map={glow}
          transparent
          opacity={0.7}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </instancedMesh>

      <instancedMesh ref={shardRef} args={[undefined, undefined, MAX_PROJECTILES]} frustumCulled={false}>
        <coneGeometry args={[1, 1, 6]} />
        <meshBasicMaterial
          transparent
          opacity={0.6}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </instancedMesh>

      <lineSegments ref={trailRef} geometry={trailGeometry} frustumCulled={false}>
        <lineBasicMaterial
          vertexColors
          transparent
          opacity={0.85}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </lineSegments>

      {/* A small pool of real lights: only the first few projectiles get one. */}
      {Array.from({ length: 4 }).map((_, i) => (
        <pointLight
          key={i}
          ref={(el) => {
            if (el) lightsRef.current[i] = el;
          }}
          visible={false}
          intensity={0}
          decay={2}
        />
      ))}
    </group>
  );
}
